import { useCallback, useEffect, useMemo, useState } from 'react';
import { I18nContext, STORAGE_KEY, type I18nValue, type Lang, type Translate } from './context';
import { DICTIONARY } from './messages';
import { setFormatLocale } from '@/lib/format';

function initialLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'th') return stored;
  // No stored choice: follow the browser rather than defaulting to English. The
  // operators running this console are in Thailand.
  return navigator.language?.toLowerCase().startsWith('th') ? 'th' : 'en';
}

/**
 * Translation for the console.
 *
 * Deliberately hand-rolled rather than react-i18next: the whole requirement is
 * two languages, one flat key space and no runtime loading, which is the thirty
 * lines below. Everything that makes an i18n library worth its weight - plural
 * category rules, lazy namespace chunks, ICU syntax - is weight we would carry
 * without ever using it.
 *
 * Interpolation is `{name}`. Plurals are NOT handled here: Thai has no plural
 * form, so a shared plural machine would exist to serve English alone. Where a
 * count changes the English wording, declare `<key>.one` and `<key>.other` and
 * pick between them at the call site, e.g.
 *
 *   t(n === 1 ? 'nav.attention.one' : 'nav.attention.other', { count: n })
 */
export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(initialLang);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    // Screen readers switch voice off this, and it drives `lang`-scoped CSS.
    // index.html sets the same attribute before first paint; this keeps it
    // current after a switch.
    document.documentElement.lang = lang;
    setFormatLocale(lang);
  }, [lang]);

  const t = useCallback<Translate>(
    (key, vars) => {
      // Falls back through Thai -> English -> the key itself. The dictionary
      // types make a miss impossible at compile time; this only decides what a
      // stale cached bundle shows rather than crashing the screen.
      const template = DICTIONARY[lang][key] ?? DICTIONARY.en[key] ?? key;
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in vars ? String(vars[name]) : match,
      );
    },
    [lang],
  );

  const value = useMemo<I18nValue>(() => ({ t, lang, setLang }), [t, lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
